import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Pause, Play } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Modal, StatusBadge, Field, inputCls, thCls, tdCls } from "./ui";

const PLACEMENTS = ["homepage_top", "homepage_mid", "category_top", "search_results", "listing_sidebar"];
const TYPES = ["banner", "sponsored_business", "featured_listing", "sponsored_job", "sponsored_event", "category_promotion", "homepage_placement"];
const EMPTY = {
  title: "", advertiser: "", type: "banner", placement: "homepage_top", image_url: "", link: "",
  country: "US", category: "", start_date: "", end_date: "", price: 0, currency: "USD",
  payment_status: "unpaid", status: "draft",
};

export default function Ads() {
  const [ads, setAds] = useState(null);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/ads").then((r) => setAds(r.data || [])).catch(() => setAds([]));
  useEffect(() => { load(); api.get("/admin/categories").then((r) => setCategories(r.data || [])).catch(() => {}); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, price: Number(form.price) || 0 };
      if (editing === "new") await api.post("/admin/ads", payload);
      else await api.put(`/admin/ads/${editing}`, payload);
      toast.success("Campaign saved.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const patch = async (id, body, msg) => {
    await api.patch(`/admin/ads/${id}`, body).catch((e) => toast.error(formatApiError(e)));
    toast.success(msg);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    await api.delete(`/admin/ads/${id}`).catch(() => {});
    load();
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="admin-ads">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-stone2 max-w-xl">Banner ads, sponsored placements and featured campaigns. Impressions and clicks are tracked automatically; mark payment once the QuickBooks invoice is settled.</p>
        <button onClick={() => { setForm(EMPTY); setEditing("new"); }} className="inline-flex items-center gap-2 bg-ruby text-cream text-sm font-semibold px-5 py-2.5 rounded-xl shrink-0" data-testid="new-ad-btn">
          <Plus className="w-4 h-4" /> New campaign
        </button>
      </div>
      <div className="bg-white rounded-xl border border-[#E6DED3] shadow-sm overflow-x-auto">
        <table className="w-full text-left min-w-[900px]" data-testid="ads-table">
          <thead className="border-b border-sand">
            <tr>
              <th className={thCls}>Campaign</th><th className={thCls}>Placement</th><th className={thCls}>Dates</th>
              <th className={thCls}>Price</th><th className={thCls}>Payment</th><th className={thCls}>Impr. / Clicks</th><th className={thCls}>Status</th><th className={thCls}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-sand">
            {(ads || []).map((a) => (
              <tr key={a.id} data-testid={`ad-row-${a.id}`}>
                <td className={tdCls}>
                  <p className="font-semibold">{a.title}</p>
                  <p className="text-xs text-stone2">{a.advertiser} · {a.type.replace(/_/g, " ")}{a.category ? ` · ${a.category}` : ""}</p>
                </td>
                <td className={`${tdCls} font-mono text-xs`}>{a.placement}</td>
                <td className={`${tdCls} text-xs`}>{a.start_date || "—"} → {a.end_date || "—"}</td>
                <td className={tdCls}>{a.currency} {a.price}</td>
                <td className={tdCls}>
                  <select
                    value={a.payment_status}
                    onChange={(e) => patch(a.id, { payment_status: e.target.value }, "Payment status updated.")}
                    className="px-2 py-1.5 rounded-lg bg-sand/60 text-xs font-semibold outline-none"
                    data-testid={`ad-payment-${a.id}`}
                  >
                    {["unpaid", "invoiced", "paid"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className={`${tdCls} text-xs`}>{a.impressions || 0} / {a.clicks || 0}</td>
                <td className={tdCls}><StatusBadge status={a.status} /></td>
                <td className={tdCls}>
                  <div className="flex gap-1">
                    {a.status === "active" ? (
                      <button title="Pause" onClick={() => patch(a.id, { status: "paused" }, "Campaign paused.")} className="p-2 text-gold-dark hover:bg-gold-light rounded-lg" data-testid={`pause-ad-${a.id}`}><Pause className="w-4 h-4" /></button>
                    ) : (
                      <button title="Activate" onClick={() => patch(a.id, { status: "active" }, "Campaign live.")} className="p-2 text-jade hover:bg-jade-light rounded-lg" data-testid={`activate-ad-${a.id}`}><Play className="w-4 h-4" /></button>
                    )}
                    <button onClick={() => { setForm({ ...EMPTY, ...a }); setEditing(a.id); }} className="p-2 text-clay hover:bg-sand rounded-lg" data-testid={`edit-ad-${a.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(a.id)} className="p-2 text-ruby hover:bg-ruby-light rounded-lg" data-testid={`delete-ad-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {ads && ads.length === 0 && <p className="px-5 py-10 text-sm text-stone2 text-center">No campaigns yet.</p>}
      </div>

      {editing && (
        <Modal title={editing === "new" ? "New campaign" : "Edit campaign"} onClose={() => setEditing(null)} wide>
          <form onSubmit={save} className="space-y-4" data-testid="ad-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Title *"><input required value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} data-testid="ad-title-input" /></Field>
              <Field label="Advertiser"><input value={form.advertiser} onChange={(e) => set("advertiser", e.target.value)} className={inputCls} data-testid="ad-advertiser-input" /></Field>
              <Field label="Type">
                <select value={form.type} onChange={(e) => set("type", e.target.value)} className={inputCls} data-testid="ad-type-select">
                  {TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                </select>
              </Field>
              <Field label="Placement">
                <select value={form.placement} onChange={(e) => set("placement", e.target.value)} className={inputCls} data-testid="ad-placement-select">
                  {PLACEMENTS.map((p) => <option key={p} value={p}>{p.replace(/_/g, " ")}</option>)}
                </select>
              </Field>
              <Field label="Category (optional)">
                <select value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls} data-testid="ad-category-select">
                  <option value="">All categories</option>
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                </select>
              </Field>
              <Field label="Country"><input value={form.country} onChange={(e) => set("country", e.target.value)} className={inputCls} data-testid="ad-country-input" /></Field>
              <Field label="Start date"><input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className={inputCls} data-testid="ad-start-input" /></Field>
              <Field label="End date"><input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className={inputCls} data-testid="ad-end-input" /></Field>
              <Field label="Price"><input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className={inputCls} data-testid="ad-price-input" /></Field>
              <Field label="Currency"><input value={form.currency} onChange={(e) => set("currency", e.target.value)} className={inputCls} data-testid="ad-currency-input" /></Field>
            </div>
            <Field label="Banner image URL"><input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} className={inputCls} data-testid="ad-image-input" /></Field>
            <Field label="Destination link"><input value={form.link} onChange={(e) => set("link", e.target.value)} className={inputCls} data-testid="ad-link-input" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Payment status">
                <select value={form.payment_status} onChange={(e) => set("payment_status", e.target.value)} className={inputCls} data-testid="ad-payment-select">
                  {["unpaid", "invoiced", "paid"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Campaign status">
                <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls} data-testid="ad-status-select">
                  {["draft", "active", "paused", "ended"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </div>
            <button type="submit" className="w-full bg-ruby text-cream font-bold py-3 rounded-xl" data-testid="ad-save-btn">Save campaign</button>
          </form>
        </Modal>
      )}
    </div>
  );
}
